import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Drawer, Empty, Modal, Space, Spin, Tabs, Tag, Typography, message } from 'antd';
import { checkBaiduBaikeEntryApi, showExpertApi } from '@services/Experts';
import OperationLogDrawer from '@/components/common/OperationLogDrawer';
import { ExpertCredibilityBadge } from '@/components/common/ExpertInfoCard';
import ExpertSectionAnchor from './ExpertSectionAnchor';
import ExpertProfileView from './ExpertProfileView';
import ExpertProfileForm from './ExpertProfileForm';
import ExpertValuePanel from './ExpertValuePanel';
import { EXPERT_PROFILE_MODE, EXPERT_PROFILE_SECTION, EXPERT_PROFILE_TAB, getExpertProfileFooterState, getExpertProfileNextStateForExpertSwitch } from './expertProfileDrawerState';
import './index.less';

const { Text } = Typography;

const SECTION_ID_MAP = {
    [EXPERT_PROFILE_SECTION.BASIC]: 'section-basic',
    [EXPERT_PROFILE_SECTION.PRO]: 'section-pro',
    [EXPERT_PROFILE_SECTION.PAYMENT]: 'section-payment',
    [EXPERT_PROFILE_SECTION.MORE]: 'section-more',
};

const SECTION_KEYS = [EXPERT_PROFILE_SECTION.BASIC, EXPERT_PROFILE_SECTION.PRO, EXPERT_PROFILE_SECTION.PAYMENT, EXPERT_PROFILE_SECTION.MORE];

/**
 * 根据滚动位置计算应对应的分区高亮。
 * 滚到底部且仍可滚动时固定为「更多信息」：末段不足一屏时标题无法滚到参考线，否则会误判为上一分区。
 */
function computeActiveSectionFromContainer(container) {
    if (!container) {
        return EXPERT_PROFILE_SECTION.BASIC;
    }

    const scrollSlack = 4;
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    const atBottom = maxScroll > 0 && maxScroll - container.scrollTop <= scrollSlack;
    if (atBottom) {
        return EXPERT_PROFILE_SECTION.MORE;
    }

    const anchorEl = container.querySelector('.expert-profile-anchor');
    const line = anchorEl ? Math.ceil(anchorEl.getBoundingClientRect().height) : 0;
    const containerTop = container.getBoundingClientRect().top;

    let currentSection = EXPERT_PROFILE_SECTION.BASIC;
    SECTION_KEYS.forEach(sectionKey => {
        const element = container.querySelector(`#${SECTION_ID_MAP[sectionKey]}`);
        if (!element) {
            return;
        }
        const sectionTop = element.getBoundingClientRect().top - containerTop;
        if (sectionTop <= line) {
            currentSection = sectionKey;
        }
    });
    return currentSection;
}

/**
 * 将目标分区滚到滚动容器可视区域顶部（无额外偏移；双帧后测量，减少布局未稳定时的偏差）
 */
function scrollSectionToContainerTop(container, target, behavior = 'smooth') {
    const run = () => {
        if (!container || !target || !document.contains(target)) {
            return;
        }
        const cRect = container.getBoundingClientRect();
        const tRect = target.getBoundingClientRect();
        const nextTop = container.scrollTop + (tRect.top - cRect.top);

        container.scrollTo({
            top: Math.max(0, Math.round(nextTop)),
            behavior,
        });
    };

    requestAnimationFrame(() => {
        requestAnimationFrame(run);
    });
}

export default function ExpertProfileDrawer({
    open,
    expertId,
    onClose,
    onRefreshList,
    canViewEvaluation = true,
    onOpenChangeCredibility,
    profileDetailRefreshNonce = 0,
    onOpenExpertDetail,
    /** 为 true 时与列表「编辑」一致：打开即进入编辑表单（与先「查看」再点「编辑」为同一套界面） */
    openInEditMode = false,
    /** 嵌套在其他全屏 Drawer 之上时使用，例如会议「添加嘉宾」内打开新增专家 */
    zIndex,
}) {
    const isCreate = expertId === 0;
    const formRef = useRef(null);
    const infoScrollRef = useRef(null);
    const requestVersionRef = useRef(0);
    /** 点击锚点触发程序化滚动时暂不参与 scroll spy，避免高亮来回闪 */
    const programmaticScrollLockRef = useRef(false);
    const programmaticScrollReleaseTimerRef = useRef(null);

    const [activeTab, setActiveTab] = useState(EXPERT_PROFILE_TAB.INFO);
    const [mode, setMode] = useState(EXPERT_PROFILE_MODE.VIEW);
    const [activeSection, setActiveSection] = useState(EXPERT_PROFILE_SECTION.BASIC);
    const [expertDetail, setExpertDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [pendingScrollReset, setPendingScrollReset] = useState(false);
    const [footerStatus, setFooterStatus] = useState(undefined);
    const [baiduBaikeUrl, setBaiduBaikeUrl] = useState('');
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);

    /** 嵌套在全屏 Drawer 内时（如会议「添加嘉宾」里打开新增专家），内层 zIndex 高于默认弹层；Modal / body 挂载的 Select 等需同步抬高，否则无法点击关闭或下拉 */
    const nestedOverlayZIndex = zIndex != null ? zIndex + 100 : undefined;

    const modalNestedZIndexProps = useMemo(() => (nestedOverlayZIndex != null ? { zIndex: nestedOverlayZIndex } : {}), [nestedOverlayZIndex]);

    const footerState = useMemo(
        () =>
            getExpertProfileFooterState({
                activeTab,
                mode,
                canEdit: true,
                hasExpertId: Number(expertId) > 0,
                hasLoadError: loadError,
                isCreate,
            }),
        [activeTab, expertId, isCreate, loadError, mode],
    );

    const fetchExpertDetail = useCallback(async currentExpertId => {
        if (!currentExpertId) {
            setExpertDetail(null);
            setLoadError(false);
            return;
        }

        const currentRequestVersion = requestVersionRef.current + 1;
        requestVersionRef.current = currentRequestVersion;
        setLoading(true);
        setLoadError(false);

        try {
            const res = await showExpertApi({ id: currentExpertId });
            if (requestVersionRef.current !== currentRequestVersion) {
                return;
            }

            if (res?.status_code === 200 && res.data) {
                setExpertDetail(res.data);
                setFooterStatus(res.data.status);
                setLoadError(false);
            } else {
                setExpertDetail(null);
                setFooterStatus(undefined);
                setLoadError(true);
            }
        } catch (_error) {
            if (requestVersionRef.current !== currentRequestVersion) {
                return;
            }
            setExpertDetail(null);
            setFooterStatus(undefined);
            setLoadError(true);
        } finally {
            if (requestVersionRef.current === currentRequestVersion) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (expertId === 0) {
            setActiveTab(EXPERT_PROFILE_TAB.INFO);
            setMode(EXPERT_PROFILE_MODE.EDIT);
            setActiveSection(EXPERT_PROFILE_SECTION.BASIC);
            setDirty(false);
            setExpertDetail(null);
            setLoadError(false);
            setLoading(false);
            setFooterStatus(undefined);
            setPendingScrollReset(true);
            return;
        }

        if (expertId == null) {
            return;
        }

        const nextState = getExpertProfileNextStateForExpertSwitch({
            nextExpertId: expertId,
        });
        setActiveTab(nextState.activeTab);
        setMode(openInEditMode ? EXPERT_PROFILE_MODE.EDIT : nextState.mode);
        setActiveSection(nextState.activeSection);
        setDirty(false);
        setPendingScrollReset(nextState.shouldScrollToTop);
        fetchExpertDetail(expertId);
    }, [expertId, fetchExpertDetail, open, openInEditMode]);

    const profileDetailRefreshNoncePrevRef = useRef(profileDetailRefreshNonce);

    /** 父级在列表外完成“调整可信度”后递增 nonce，仅在此变化时刷新详情（避免 nonce 恒为正时每次打开抽屉都重复请求） */
    useEffect(() => {
        if (profileDetailRefreshNonce === profileDetailRefreshNoncePrevRef.current) {
            return;
        }
        profileDetailRefreshNoncePrevRef.current = profileDetailRefreshNonce;
        if (!profileDetailRefreshNonce || !open || !expertId || expertId === 0) {
            return;
        }
        fetchExpertDetail(expertId);
    }, [profileDetailRefreshNonce, expertId, fetchExpertDetail, open]);

    useEffect(() => {
        if (!open || !pendingScrollReset || loading) {
            return;
        }

        const timer = window.requestAnimationFrame(() => {
            if (infoScrollRef.current) {
                infoScrollRef.current.scrollTop = 0;
            }
            setActiveSection(EXPERT_PROFILE_SECTION.BASIC);
            setPendingScrollReset(false);
        });

        return () => {
            window.cancelAnimationFrame(timer);
        };
    }, [loading, open, pendingScrollReset]);

    useEffect(() => {
        if (!open || isCreate) {
            setBaiduBaikeUrl('');
            return;
        }

        const expertName = expertDetail?.memUsername;
        if (!expertName) {
            setBaiduBaikeUrl('');
            return;
        }

        let cancelled = false;

        const checkBaiduBaike = async () => {
            try {
                const res = await checkBaiduBaikeEntryApi({ keyword: expertName });
                if (cancelled) {
                    return;
                }
                if (res?.status_code === 200 && res.data) {
                    setBaiduBaikeUrl(`https://baike.baidu.com/item/${encodeURIComponent(expertName)}`);
                } else {
                    setBaiduBaikeUrl('');
                }
            } catch (_error) {
                if (!cancelled) {
                    setBaiduBaikeUrl('');
                }
            }
        };

        checkBaiduBaike();

        return () => {
            cancelled = true;
        };
    }, [expertDetail?.memUsername, isCreate, open]);

    useEffect(() => {
        if (!open || activeTab !== EXPERT_PROFILE_TAB.INFO || !infoScrollRef.current) {
            return;
        }

        const handleScroll = () => {
            if (programmaticScrollLockRef.current) {
                return;
            }
            const container = infoScrollRef.current;
            if (!container) {
                return;
            }

            setActiveSection(computeActiveSectionFromContainer(container));
        };

        const currentContainer = infoScrollRef.current;
        currentContainer.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            currentContainer.removeEventListener('scroll', handleScroll);
        };
    }, [activeTab, open]);

    const clearProgrammaticScrollReleaseTimer = useCallback(() => {
        if (programmaticScrollReleaseTimerRef.current != null) {
            window.clearTimeout(programmaticScrollReleaseTimerRef.current);
            programmaticScrollReleaseTimerRef.current = null;
        }
    }, []);

    const releaseProgrammaticScrollLockAndSync = useCallback(() => {
        programmaticScrollLockRef.current = false;
        clearProgrammaticScrollReleaseTimer();
        const container = infoScrollRef.current;
        if (container) {
            setActiveSection(computeActiveSectionFromContainer(container));
        }
    }, [clearProgrammaticScrollReleaseTimer]);

    const handleInfoSectionChange = sectionKey => {
        clearProgrammaticScrollReleaseTimer();

        programmaticScrollLockRef.current = true;
        setActiveSection(sectionKey);

        const container = infoScrollRef.current;
        if (!container) {
            programmaticScrollLockRef.current = false;
            return;
        }

        // 基本信息：滚回顶部
        if (sectionKey === EXPERT_PROFILE_SECTION.BASIC) {
            container.scrollTo({ top: 0, behavior: 'auto' });
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(releaseProgrammaticScrollLockAndSync);
            });
            return;
        }

        const sectionId = SECTION_ID_MAP[sectionKey];
        const target = container.querySelector(`#${sectionId}`);
        if (!target) {
            releaseProgrammaticScrollLockAndSync();
            return;
        }

        const onScrollEnd = () => {
            container.removeEventListener('scrollend', onScrollEnd);
            clearProgrammaticScrollReleaseTimer();
            releaseProgrammaticScrollLockAndSync();
        };
        container.addEventListener('scrollend', onScrollEnd, { once: true });
        programmaticScrollReleaseTimerRef.current = window.setTimeout(() => {
            container.removeEventListener('scrollend', onScrollEnd);
            releaseProgrammaticScrollLockAndSync();
        }, 600);

        scrollSectionToContainerTop(container, target, 'smooth');
    };

    const handleClose = useCallback(() => {
        if (saving) {
            return;
        }

        if (mode === EXPERT_PROFILE_MODE.EDIT && dirty) {
            Modal.confirm({
                title: '确认关闭吗？',
                content: '当前有未保存的修改，关闭后将丢失这些内容。',
                okText: '确认关闭',
                cancelText: '继续编辑',
                ...modalNestedZIndexProps,
                onOk: () => {
                    if (!isCreate) {
                        setMode(EXPERT_PROFILE_MODE.VIEW);
                    }
                    setDirty(false);
                    onClose?.();
                },
            });
            return;
        }

        onClose?.();
    }, [dirty, isCreate, modalNestedZIndexProps, mode, onClose, saving]);

    const handleStartEdit = () => {
        if (loading || loadError) {
            return;
        }

        setMode(EXPERT_PROFILE_MODE.EDIT);
        setActiveTab(EXPERT_PROFILE_TAB.INFO);
        setDirty(false);
    };

    const handleCancelEdit = () => {
        if (saving) {
            return;
        }

        if (isCreate) {
            handleClose();
            return;
        }

        if (dirty) {
            Modal.confirm({
                title: '确认取消编辑吗？',
                content: '未保存的修改将会丢失。',
                okText: '确认取消',
                cancelText: '继续编辑',
                ...modalNestedZIndexProps,
                onOk: () => {
                    setMode(EXPERT_PROFILE_MODE.VIEW);
                    setDirty(false);
                    setFooterStatus(expertDetail?.status);
                },
            });
            return;
        }

        setMode(EXPERT_PROFILE_MODE.VIEW);
        setFooterStatus(expertDetail?.status);
    };

    const handleSave = async () => {
        if (!formRef.current?.submit || saving) {
            return;
        }

        setSaving(true);
        try {
            await formRef.current.submit();
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSuccess = async () => {
        setDirty(false);

        if (expertId === 0) {
            message.success('新增专家成功');
        } else {
            message.success('专家信息保存成功');
        }

        onClose?.();
        await onRefreshList?.();
    };

    const handleViewExpertDetailFromForm = useCallback(
        nextId => {
            if (!nextId) {
                return;
            }
            onOpenExpertDetail?.(nextId);
        },
        [onOpenExpertDetail],
    );

    const handleChangeCredibility = useCallback(() => {
        const id = expertDetail?.id ?? expertDetail?.expertId;
        if (!id) {
            return;
        }
        onOpenChangeCredibility?.({ ...expertDetail, id });
    }, [expertDetail, onOpenChangeCredibility]);

    const renderFooterLeft = () => {
        if (!footerState.showLeftMeta) {
            return <span />;
        }

        const currentStatus = footerStatus ?? expertDetail?.status;

        return (
            <Space size={16}>
                {currentStatus === 1 && (
                    <span>
                        <span className="expert-profile-drawer__footer-label">专家状态：</span>
                        <Tag color="#f50" className="expert-profile-drawer__footer-status-tag">
                            禁用
                        </Tag>
                    </span>
                )}
                <span>
                    <span className="expert-profile-drawer__footer-label">可信度：</span>
                    <ExpertCredibilityBadge credibility={expertDetail?.credibility} />
                    <Button type="link" onClick={handleChangeCredibility}>
                        调整可信度
                    </Button>
                </span>
            </Space>
        );
    };

    const renderFooterRight = () => {
        if (footerState.showSaveAction) {
            return (
                <Space>
                    <Button onClick={handleCancelEdit} disabled={saving}>
                        {isCreate ? '取消' : '取消编辑'}
                    </Button>
                    <Button type="primary" onClick={handleSave} loading={saving}>
                        保存
                    </Button>
                </Space>
            );
        }

        if (footerState.showCancelCloseAction || footerState.showEditAction) {
            return (
                <Space>
                    {footerState.showCancelCloseAction ? (
                        <Button onClick={handleClose} disabled={saving}>
                            取消
                        </Button>
                    ) : null}
                    {footerState.showEditAction ? (
                        <Button type="primary" onClick={handleStartEdit} disabled={loading}>
                            编辑
                        </Button>
                    ) : null}
                </Space>
            );
        }

        return null;
    };

    const tabItems = [
        {
            key: EXPERT_PROFILE_TAB.INFO,
            label: '专家信息',
            children: (
                <div className="expert-profile-drawer__tab-pane">
                    <div ref={infoScrollRef} className="expert-profile-drawer__info-scroll">
                        <ExpertSectionAnchor activeSection={activeSection} onSectionChange={handleInfoSectionChange} />
                        <div className="expert-profile-drawer__info-scroll-body">
                            {mode === EXPERT_PROFILE_MODE.EDIT ? (
                                <ExpertProfileForm ref={formRef} expertId={expertId} onSaveSuccess={handleSaveSuccess} onDirtyChange={setDirty} onStatusChange={setFooterStatus} onViewExpertDetail={handleViewExpertDetailFromForm} />
                            ) : (
                                <ExpertProfileView expert={expertDetail} mode={mode} />
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    if (canViewEvaluation && Number(expertId) > 0) {
        tabItems.push({
            key: EXPERT_PROFILE_TAB.EVALUATION,
            label: '专家价值',
            children: (
                <div className="expert-profile-drawer__tab-pane expert-profile-drawer__tab-pane--evaluation">
                    <ExpertValuePanel expertId={expertId} />
                </div>
            ),
        });
    }

    const tabBarExtraContent =
        Number(expertId) > 0 ? (
            <Space size="medium" align="center" className="expert-profile-drawer__tab-extra">
                <Button type="link" onClick={() => setLogDrawerOpen(true)}>
                    操作记录
                </Button>
                {baiduBaikeUrl ? (
                    <a href={baiduBaikeUrl} target="_blank" rel="noreferrer">
                        查看百度百科
                    </a>
                ) : null}
            </Space>
        ) : null;

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                className="expert-profile-drawer"
                placement="bottom"
                open={open}
                onClose={handleClose}
                mask={{ closable: false }}
                destroyOnHidden
                zIndex={zIndex}
                styles={{
                    body: { padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
                    wrapper: { height: '100vh' },
                }}
                title={
                    <div className="expert-profile-drawer__title">
                        <button type="button" className="drawer-back" onClick={handleClose} aria-label="返回上一界面">
                            <i className="bi bi-chevron-left" aria-hidden="true" />
                        </button>
                        <div className="expert-profile-drawer__title-main">
                            <span>{isCreate ? '新增专家' : expertDetail?.memUsername || '专家信息'}</span>
                            {!isCreate && !!expertId ? <Text type="secondary">#{expertId}</Text> : null}
                        </div>
                    </div>
                }
                footer={
                    <div className="expert-profile-drawer__footer">
                        <div className="expert-profile-drawer__footer-left">{renderFooterLeft()}</div>
                        <div className="expert-profile-drawer__footer-right">{renderFooterRight()}</div>
                    </div>
                }
            >
                <ConfigProvider
                    getPopupContainer={trigger => {
                        // 嵌套 Drawer 时，下拉若挂到 body 则 z-index 低于内层 Drawer（约 1100），无法展开/点击；挂到本抽屉 body 内可避免被遮挡
                        const body = trigger?.closest?.('.ant-drawer-body');
                        return body || document.body;
                    }}
                >
                    <div className="expert-profile-drawer__body">
                        <Spin spinning={loading} description="正在加载专家信息...">
                            {loadError && !isCreate ? (
                                <div className="expert-profile-drawer__error">
                                    <Empty description="专家信息加载失败，请重试" />
                                    <Button type="primary" onClick={() => fetchExpertDetail(expertId)}>
                                        重试
                                    </Button>
                                </div>
                            ) : (
                                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} tabBarExtraContent={tabBarExtraContent} />
                            )}
                        </Spin>
                    </div>
                </ConfigProvider>
            </Drawer>
            {Number(expertId) > 0 ? <OperationLogDrawer open={logDrawerOpen} onClose={() => setLogDrawerOpen(false)} entityType="expert" entityId={expertId} module="expert" /> : null}
        </>
    );
}
